import { lazy, Suspense } from "react";
import { LoadingIndicator as Loader2 } from "@/components/ui/loading-indicator";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { DataProvider } from "@/contexts/DataContext";
import AdminLayout from "@/components/AdminLayout";
import {
  loadAnalyticsPage,
  loadApiTesterPage,
  loadBillingPage,
  loadBookingsPage,
  loadClientsPage,
  loadEmailCampaignPage,
  loadEmailTemplatesPage,
  loadPublicLegalPage,
  loadEventFormPage,
  loadEventTypesPage,
  loadLoginPage,
  loadNotFoundPage,
  loadOperationDetailsPage,
  loadPointsOfSalePage,
  loadPublicAppleRepairPricingPage,
  loadPublicBookingPage,
  loadPublicManageBookingPage,
  loadPublicProfilePage,
  loadSettingsPage,
  loadSitesPage,
  loadStatisticsPage,
  loadStatusPage,
  loadTransactionsPage,
  withChunkRetry,
} from "@/lib/route-loaders";

const LoginPage = lazy(withChunkRetry(loadLoginPage));
const EventTypesPage = lazy(withChunkRetry(loadEventTypesPage));
const EventFormPage = lazy(withChunkRetry(loadEventFormPage));
const BookingsPage = lazy(withChunkRetry(loadBookingsPage));
const PublicBookingPage = lazy(withChunkRetry(loadPublicBookingPage));
const PublicProfilePage = lazy(withChunkRetry(loadPublicProfilePage));
const PublicAppleRepairPricingPage = lazy(withChunkRetry(loadPublicAppleRepairPricingPage));
const PublicManageBookingPage = lazy(withChunkRetry(loadPublicManageBookingPage));
const SettingsPage = lazy(withChunkRetry(loadSettingsPage));
const ClientsPage = lazy(withChunkRetry(loadClientsPage));
const StatisticsPage = lazy(withChunkRetry(loadStatisticsPage));
const AnalyticsPage = lazy(withChunkRetry(loadAnalyticsPage));
const StatusPage = lazy(withChunkRetry(loadStatusPage));
const ApiTesterPage = lazy(withChunkRetry(loadApiTesterPage));
const EmailCampaignPage = lazy(withChunkRetry(loadEmailCampaignPage));
const EmailTemplatesPage = lazy(withChunkRetry(loadEmailTemplatesPage));
const PublicLegalPage = lazy(withChunkRetry(loadPublicLegalPage));
const SitesPage = lazy(withChunkRetry(loadSitesPage));
const BillingPage = lazy(withChunkRetry(loadBillingPage));
const TransactionsPage = lazy(withChunkRetry(loadTransactionsPage));
const OperationDetailsPage = lazy(withChunkRetry(loadOperationDetailsPage));
const PointsOfSalePage = lazy(withChunkRetry(loadPointsOfSalePage));
const NotFound = lazy(withChunkRetry(loadNotFoundPage));

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center bg-[#f8f9fa]">
    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
  </div>
);

const AdminPageFallback = () => (
  <div className="flex min-h-[55dvh] items-center justify-center">
    <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
  </div>
);

const adminPage = (page: React.ReactNode) => (
  <Suspense fallback={<AdminPageFallback />}>{page}</Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <DataProvider>
          <Routes>
            <Route path="/" element={<Suspense fallback={<LoadingFallback />}><PublicProfilePage /></Suspense>} />
            <Route path="/electrodepot" element={<Suspense fallback={<LoadingFallback />}><PublicProfilePage brand="electrodepot" /></Suspense>} />
            <Route path="/tarifs-apple" element={<Suspense fallback={<LoadingFallback />}><PublicAppleRepairPricingPage /></Suspense>} />
            <Route path="/mentions-legales" element={<Suspense fallback={<LoadingFallback />}><PublicLegalPage page="mentions" /></Suspense>} />
            <Route path="/confidentialite" element={<Suspense fallback={<LoadingFallback />}><PublicLegalPage page="confidentialite" /></Suspense>} />
            <Route path="/garantie" element={<Suspense fallback={<LoadingFallback />}><PublicLegalPage page="garantie" /></Suspense>} />
            <Route path="/login" element={<Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>} />
            <Route path="/reservation/:slug" element={<Suspense fallback={<LoadingFallback />}><PublicBookingPage /></Suspense>} />
            <Route path="/gestion-rdv/:token" element={<Suspense fallback={<LoadingFallback />}><PublicManageBookingPage /></Suspense>} />
            <Route element={<AdminLayout />}>
              <Route path="/dashboard" element={<Navigate to="/dashboard/analytics" replace />} />
              <Route path="/dashboard/events" element={adminPage(<EventTypesPage />)} />
              <Route path="/dashboard/events/new" element={adminPage(<EventFormPage />)} />
              <Route path="/dashboard/events/:id" element={adminPage(<EventFormPage />)} />
              <Route path="/dashboard/bookings" element={adminPage(<BookingsPage />)} />
              <Route path="/dashboard/campaigns" element={adminPage(<EmailCampaignPage />)} />
              <Route path="/dashboard/emails" element={adminPage(<EmailTemplatesPage />)} />
              <Route path="/dashboard/clients" element={adminPage(<ClientsPage />)} />
              <Route path="/dashboard/analytics" element={adminPage(<AnalyticsPage />)} />
              <Route path="/dashboard/statistics" element={adminPage(<StatisticsPage />)} />
              <Route path="/dashboard/billing" element={adminPage(<BillingPage />)} />
              <Route path="/dashboard/transactions" element={adminPage(<TransactionsPage />)} />
              <Route path="/dashboard/operations" element={adminPage(<OperationDetailsPage />)} />
              <Route path="/dashboard/upcoming-operations" element={adminPage(<OperationDetailsPage key="upcoming" scope="upcoming" />)} />
              <Route path="/dashboard/sites" element={adminPage(<SitesPage />)} />
              <Route path="/dashboard/points-of-sale" element={adminPage(<PointsOfSalePage />)} />
              <Route path="/dashboard/settings" element={adminPage(<SettingsPage />)} />
              <Route path="/dashboard/status" element={adminPage(<StatusPage />)} />
              <Route path="/dashboard/api-tester" element={adminPage(<ApiTesterPage />)} />
            </Route>
            <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
          </Routes>
        </DataProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
